import React, { useEffect } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Divider,
} from '@mui/material'
import { Close, Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ApplicationState } from '@/state/root.models'
import { ADMIN_VIEW } from '@/constants/brand'
import { useFormik, FormikProvider } from 'formik'

import PlayerIdFilterType from './player-id.component'
import GamesFilterType from './game.component'
import GamesCategoryFilterType from './game-category.component'
import GamesProviderFilterType from './game-provider.component'
import BalanceFilterType from './balance.component'
import BrandFilterType from './brand.component'

interface ReportsFiltersDrawerProps {
  open: boolean
  onClose: () => void
  onApply: (filters: any) => void
  onClear: () => void
  currentFilters: any
}

export const ReportsFiltersDrawer = ({
  open,
  onClose,
  onApply,
  onClear,
  currentFilters,
}: ReportsFiltersDrawerProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { brand } = useSelector(({ auth }: ApplicationState) => auth)
  const isPlayerCard = location.pathname.includes('/player-card')

  const localFormik = useFormik({
    initialValues: currentFilters || {},
    enableReinitialize: true,
    onSubmit: () => {},
  })

  useEffect(() => {
    if (open) {
      localFormik.resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleChangeSearch = (field: string, value: any) => {
    localFormik.setFieldValue(field, value)
  }

  const handleApply = () => {
    onApply(localFormik.values)
    onClose()
  }

  const handleClear = () => {
    onClear()
    onClose()
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <FormikProvider value={localFormik}>
        <Box
          width={400}
          p={2}
          display="flex"
          flexDirection="column"
          height="100%"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={onClose} edge="start" sx={{ mr: 1 }}>
                <Close />
              </IconButton>
              <Typography variant="h6" fontWeight="bold">
                {t('transaction.filters')}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box flex={1} sx={{ overflowY: 'auto' }}>
            <Grid container spacing={2}>
              {!isPlayerCard && (
                <Grid item xs={12}>
                  <PlayerIdFilterType handleChangeSearch={handleChangeSearch} />
                </Grid>
              )}
              <Grid item xs={12}>
                <GamesFilterType handleChangeSearch={handleChangeSearch} />
              </Grid>
              <Grid item xs={12}>
                <GamesCategoryFilterType
                  handleChangeSearch={handleChangeSearch}
                />
              </Grid>
              <Grid item xs={12}>
                <GamesProviderFilterType
                  handleChangeSearch={handleChangeSearch}
                />
              </Grid>
              <Grid item xs={12}>
                <BalanceFilterType handleChangeSearch={handleChangeSearch} />
              </Grid>
              {brand.siteId === ADMIN_VIEW && (
                <Grid item xs={12}>
                  <BrandFilterType handleChangeSearch={handleChangeSearch} />
                </Grid>
              )}
            </Grid>
          </Box>

          <Box display="flex" justifyContent="space-between" mt={2} pt={2}>
            <Button
              startIcon={<Delete />}
              color="error"
              onClick={handleClear}
              sx={{
                textTransform: 'none',
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.lighter',
                },
              }}
            >
              {t('labels.clear')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
              sx={{ px: 2 }}
            >
              {t('labels.applyFilter')}
            </Button>
          </Box>
        </Box>
      </FormikProvider>
    </Drawer>
  )
}
